// SurfaceDecalGob + ScorchGob — ground decals. Sim-relevant: gids + master-list slots +
// the scorch sequence counter (oldest-scorch eviction order) + the scorch bitmap SIZES
// (the dying-structure size-rejection loop consumes sync RNG per iteration).
// game/GameObjects.cpp:578-727.

import type { SimContext } from '../context.ts';
import { Gob, kfGobLayerSurfaceDecal } from './gob.ts';
import { kgtSurfaceDecal, kgtScorch } from './gobtypes.ts';
import { WcFromTc } from '../math/coords.ts';
import { IniScanf } from '../data/ini.ts';

// game/Unit.cpp:192-195 — gaptbmScorches load order (scorch_8x8/16x16/32x16/48x48.png).
// The sizes the SIM sees are TBitmap::GetSize = cx_orig/cy_orig from the shipping
// atlasmap.json (htdata832atlas.pdb), which stores this art at TWICE the filename size
// (16x16, 32x32, 64x32, 96x96) — the struct-dying scorch-pick rejection loop draws
// sync-RNG per rejection, so these exact values are part of the hash contract
// (easy-2p-b diverged at update 6417 with the halved filename sizes).
export const kScorchSizesPx: { cx: number; cy: number }[] = [
  { cx: 16, cy: 16 },
  { cx: 32, cy: 32 },
  { cx: 64, cy: 32 },
  { cx: 96, cy: 96 },
];

export class SurfaceDecalGob extends Gob {
  m_szBitmap: string | null = null;

  constructor(ctx: SimContext) {
    // :584-588 — the ctor sets the surface-decal layer bit
    super(ctx);
    this.m_ff = (this.m_ff | kfGobLayerSurfaceDecal) >>> 0;
  }

  override GetType(): number {
    return kgtSurfaceDecal;
  } // :639-642

  // game/GameObjects.cpp:594-605 — "%*d ,%s,%d ,%d"
  InitFromArgs(value: string): boolean {
    const { count, values } = IniScanf(value, '%*d ,%s,%d ,%d');
    if (count < 3) return false;
    const [szBitmap, tx, ty] = values as [string, number, number];
    return this.Init(WcFromTc(tx), WcFromTc(ty), 0, szBitmap);
  }

  // game/GameObjects.cpp:607-630 (the TBitmap is render; the name/index is retained)
  Init(wx: number, wy: number, ff: number, pszBitmap: string | null): boolean {
    this.m_wx = wx;
    this.m_wy = wy;
    this.m_szBitmap = pszBitmap;
    this.m_ff = (this.m_ff | ff) >>> 0;
    this.ctx.gobm.AddGob(this);
    return true;
  }

  IsSavable(): boolean {
    return false;
  } // :632-637
}

export class ScorchGob extends SurfaceDecalGob {
  m_nScorch = 0;
  m_nSequence: number;

  // game/GameObjects.cpp:674-678 — gnSequenceScorch is a global counter (ctx-owned)
  constructor(ctx: SimContext) {
    super(ctx);
    this.m_nSequence = ctx.gnSequenceScorch++;
  }

  // game/GameObjects.cpp:696-715 (knVerScorchGobState 2) — bumps the shared scorch
  // sequence high-water mark
  override LoadState(pstm: import('../sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 2) return false;
    this.m_nScorch = pstm.ReadWord(); // int in C++ — ZERO-extends (GameObjects.cpp:702)
    this.m_nSequence = pstm.ReadWord(); // int in C++ — ZERO-extends (GameObjects.cpp:704)
    if (this.m_nSequence > this.ctx.gnSequenceScorch) this.ctx.gnSequenceScorch = this.m_nSequence;
    return super.LoadState(pstm);
  }

  override SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    pstm.WriteByte(2);
    pstm.WriteWord(this.m_nScorch);
    pstm.WriteWord(this.m_nSequence);
    return super.SaveState(pstm);
  }

  GetSequence(): number {
    return this.m_nSequence;
  } // :680-683

  override GetType(): number {
    return kgtScorch;
  } // :685-688

  // game/GameObjects.cpp:690-694
  InitScorch(wx: number, wy: number, nScorch: number): boolean {
    this.m_nScorch = nScorch;
    return this.Init(wx, wy, 0, null); // bitmap = gaptbmScorches[nScorch] (render)
  }

  override IsSavable(): boolean {
    return true;
  } // :722-725
}

// self-register (see factory.ts)
import { registerGobType } from './factory.ts';
registerGobType(kgtSurfaceDecal, (ctx) => new SurfaceDecalGob(ctx));
registerGobType(kgtScorch, (ctx) => new ScorchGob(ctx));
